import React, { useState, useEffect } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import Competitor from 'components/presentation/Competitor';
import Noc from 'components/presentation/Noc';
import { useViewport } from 'components/general/useViewPort';
import SportCode from 'components/common/SportCode'
import NotAvailable from 'components/common/NotAvailable';
import { useUrlFriendlyToParameters } from "helpers/urlFriendly";


const getPeriodOrder = (periodsOrder, period) => {
  return periodsOrder?.find(i => i.period === period)?.order;
}

const RenderAccordion = ({ data, config, periods }) => {
  //State that keeps the active key
  const [activeKey, setActiveKey] = useState(periods ? periods[0]?.subcode : '');

  //Used to set the most recent period open when there is a new period
  useEffect(() => {
    setActiveKey(periods ? periods[0]?.subcode : '')
  }, [periods?.length])

  const handleClick = (e) => {
    setActiveKey(e)
  }
  const handleSelect = (eventKey) => setActiveKey(eventKey);

  let out = (<NotAvailable />)

  if (data?.results?.playByPlay?.length > 0) {
    if (config?.disableAccordion) {
      out = (
        <RenderOnlyPeriods periods={periods} home={data.results.home} away={data.results.away} config={config} />
      )
    } else {
      out = (
        <Accordion defaultActiveKey={periods[0]?.subcode} activeKey={activeKey} onSelect={handleSelect}>
          <RenderPeriods periods={periods} home={data.results.home} away={data.results.away} config={config} handleClick={handleClick} />
        </Accordion>
      )
    }
  }
  return out;
}

const RenderPeriods = ({ periods, home, away, config }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()
  const RenderHeader = config?.headerRender ? config.headerRender : DefaultHeaderRender;
  return periods?.map(item => {
    return (
      <Accordion.Item id={`mirs-accordion-item-${item.subcode}`} eventKey={item.subcode} key={item.subcode}>
        <Accordion.Header><SportCode discipline={mirs_discipline} entity='@Period' code={item.subcode} /></Accordion.Header>
        <Accordion.Body>
          <RenderHeader home={home} away={away} />
          <RenderSinglePeriod actions={item.actions} home={home} away={away} config={config} />
        </Accordion.Body>
      </Accordion.Item>
    )
  }
  )
}

const RenderOnlyPeriods = ({ periods, home, away, config }) => {
  const RenderHeader = config?.headerRender ? config.headerRender : DefaultHeaderRender;
  return periods?.map((item, index) => {
    return (
      <React.Fragment key={'RenderOnly' + index}>
        <RenderHeader home={home} away={away} />
        <RenderSinglePeriod actions={item.actions} home={home} away={away} config={config} />
      </React.Fragment>
    )
  }
  )
}

const DefaultHeaderRender = ({ home, away }) => {
  const { breakPoint } = useViewport();
  if (breakPoint === "sm" || breakPoint === "xs") {
    return ''
  }
  return (
    <div className="row">
      <div className="col-5"><div className='d-flex justify-content-start'><Noc className='me-1' noc={home?.participant?.organisation?.code} /><Competitor {...home?.participant} hidePicture={true} /></div></div>
      <div className="col-2"></div>
      <div className="col-5"><div className='d-flex justify-content-start'><Noc className='me-1' noc={away?.participant?.organisation?.code} /><Competitor {...home?.away} hidePicture={true} /></div></div>
    </div>
  )
}

const RenderAction = ({ action, home, away, config }) => {

  const { breakPoint } = useViewport();
  const SideRender = config?.sideRender ? config.sideRender : DefaultRenderSide;
  const CenterRender = config?.centerRender ? config.centerRender : DefaultRenderCenter
  const showHyphen = config?.showHyphen === undefined ? false : config.showHyphen;


  const pointsCenterXl = (
    <div className="row" key={action.pbpa_id}>
      <div className="col-5 "><SideRender action={action} homeAway={home} home={home} away={away} showHyphen={showHyphen} /></div>
      <div className="col-2"><CenterRender action={action} /></div>
      <div className="col-5 "><SideRender action={action} homeAway={away} home={home} away={away} showHyphen={showHyphen} /></div>
    </div>
  )

  const pointsCenterSm = (
    <div className="row" key={action.pbpa_id}>
      <div className="col-3"><CenterRender action={action} /></div>
      <div className="col-9 ">
        <SideRender action={action} homeAway={home} home={home} away={away} showHyphen={showHyphen} />
        <SideRender action={action} homeAway={away} home={home} away={away} showHyphen={showHyphen} />
      </div>
    </div>
  )

  const pointsLeft = (
    <div className="row" key={action.pbpa_id}>
      <div className="col-3"><CenterRender action={action} /></div>
      <div className="col-9 ">
        <SideRender action={action} homeAway={home} home={home} away={away} layout="points_left_up" showHyphen={showHyphen} />
        <SideRender action={action} homeAway={away} home={home} away={away} layout="points_left_down" showHyphen={showHyphen} />
      </div>
    </div>
  )


  const pointsOneLine = (
    <div className="row" key={action.pbpa_id}>
      <div className="col-2 text-center">
        <CenterRender action={action} />
      </div>
      <div className="col-5 ">
        <SideRender action={action} homeAway={home} home={home} away={away} />
      </div>
      <div className="col-5 ">
        <SideRender action={action} homeAway={away} home={home} away={away} />
      </div>
    </div>
  )

  const xl = config?.layout === 'points_left' ? pointsLeft : config?.layout === 'points_one_line' ? pointsOneLine : pointsCenterXl
  const sm = config?.layout === 'points_left' ? pointsLeft : config?.layout === 'points_one_line' ? pointsOneLine : pointsCenterSm

  return breakPoint === "sm" || breakPoint === "xs" ? sm : xl;

}

const RenderSinglePeriod = ({ actions, home, away, config }) => {
  return actions?.map(item => {
    return <RenderAction action={item} home={home} away={away} config={config} key={item.pbpa_id} />
  })
}

const DefaultRenderSide = ({ action, homeAway, home, away, layout, showHyphen }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()
  const comp = action?.competitors?.find(item => item.pbpc_code === homeAway.participant?.code)
  let className = 'box';
  if (action?.competitors?.find(item => item.pbpc_code === home.participant?.code) && action?.competitors?.find(item => item.pbpc_code === away.participant?.code)) {
    if (layout === 'points_left_up') {
      className = 'box-bottom-no-border'
    } else if (layout === 'points_left_down') {
      className = 'box-top-no-border'
    }
  }

  if (!comp) return; //do nothing if there is no competitor related to the action

  return (
    <div className={className}>
      <div><DefaultRenderCompetitor comp={comp} homeAway={homeAway} action={action} showHyphen={showHyphen} /></div>
      <div>
        <SportCode discipline={mirs_discipline} entity='@Action' code={action?.pbpa_Action} />&nbsp;
        <SportCode discipline={mirs_discipline} entity='@ResAction' code={action?.pbpa_Result} />&nbsp;
      </div>
    </div>
  )
}

export const DefaultRenderCompetitor = ({ comp, homeAway, action, showBib, showHyphen }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()
  let hyphen = showHyphen ? '- ' : '';
  if (comp.athletes) {
    return comp.athletes?.map((ath) => {
      let bib = <><span className='ath-bib'>{ath.pbpat_bib}</span>&nbsp;</>
      return (
        <div key={action?.pbpa_id + ath.pbpat_code}>
          <div className="d-flex flex-row">
            {showBib || showBib === undefined ? bib : ''}
            <span><DefaultRenderAthlete code={ath.pbpat_code} homeAway={homeAway} /></span>&nbsp;{hyphen}&nbsp;
            <span>{(action?.pbpa_Action === 'SUBST' || ath.pbpat_role === 'ASSIST') && <SportCode discipline={mirs_discipline} entity='@ActionRole' code={ath.pbpat_role} />}</span>
          </div>
        </div>
      );
    });
  } else {
    return (
      <div className='d-flex justify-content-start'>
        <Competitor hidePicture={true} alwaysNoc={true} {...homeAway?.participant} />&nbsp;{hyphen}&nbsp;
      </div>
    )
  }
}

export const DefaultRenderAthlete = ({ code, homeAway }) => {
  const ath = homeAway?.teamAthletes?.find(item => item?.athlete?.code === code);
  return (
    <Competitor hidePicture={true} alwaysNoc={true} {...ath?.athlete} />
  )
}

const DefaultRenderCenter = ({ action }) => {
  let [a, b, c] = ['', '', ''];

  if (action.competitors === undefined) a = (<div>{`${action?.pbpa_ActionDesc}  (${action?.pbpa_Action})`}</div>);

  if (action?.pbpa_When) b = (<div>{action?.pbpa_When}</div>);

  if (action.pbpa_ScoreH) c = (<div className='fw-bolder'>{action?.pbpa_ScoreH} - {action?.pbpa_ScoreA}</div>);

  return (<div className='box-center text-center'><strong>{a}{b}{c}</strong></div>);
}

const Pbp = ({ data, config }) => {
  const { mirs_rsc } = useUrlFriendlyToParameters()

  let periods = data.results.playByPlay;
  if (config.reverseSorting) {
    periods?.sort((a, b) => getPeriodOrder(config?.periodsOrder, b.subcode) - getPeriodOrder(config?.periodsOrder, a.subcode));
  } else {
    periods?.sort((a, b) => getPeriodOrder(config?.periodsOrder, a.subcode) - getPeriodOrder(config?.periodsOrder, b.subcode));
  }

  return (
    <div className="pbp-main" id={`mirs-accordion-play-by-play-${mirs_rsc}`}>
      <RenderAccordion data={data} config={config} periods={periods} />
    </div>
  )

}

export default Pbp;

